import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Project, V3Event, V3Room, V3User } from "../types/v3types";
import { useAuth } from "../hooks/useAuth";
import styled from "styled-components";

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Username = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const LogoutButton = styled.button`
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Content = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Section = styled.section`
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin: 0;
  color: #333;
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: #5a67d8;
  }
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const EventCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const EventName = styled.h3`
  font-size: 18px;
  margin: 0 0 8px 0;
  color: #333;
`;

const EventInfo = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
`;

const RoomList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RoomCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
`;

const RoomInfo = styled.div`
  flex: 1;
`;

const RoomName = styled.h4`
  font-size: 16px;
  margin: 0 0 4px 0;
  color: #333;
`;

const RoomStatus = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 8px;
  background-color: ${props => {
    switch (props.status) {
      case "waiting": return "#ffc107";
      case "running": return "#28a745";
      case "finished": return "#6c757d";
      default: return "#6c757d";
    }
  }};
  color: white;
`;

const JoinButton = styled.button`
  padding: 8px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;

  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const V3MyPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<V3User | null>(null);
  const [project, setProject] = useState<V3Project | null>(null);
  const [availableEvents, setAvailableEvents] = useState<V3Event[]>([]);
  const [myEvents, setMyEvents] = useState<V3Event[]>([]);
  const [activeRooms, setActiveRooms] = useState<V3Room[]>([]);

  useEffect(() => {
    if (!projectId) {
      navigate("/");
      return;
    }

    if (authLoading) return;

    if (!firebaseUser) {
      navigate(`/v3/${projectId}/start`);
      return;
    }

    const userId = firebaseUser.uid + "_" + projectId;

    // Load user data
    const loadUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, "v3users", userId));
        if (userDoc.exists()) {
          setUser(userDoc.data() as V3User);
        } else {
          navigate(`/v3/${projectId}/start`);
          return;
        }

        const projectDoc = await getDoc(doc(db, "v3projects", projectId));
        if (projectDoc.exists()) {
          setProject(projectDoc.data() as V3Project);
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };

    loadUserData();

    // Subscribe to events
    const eventsQuery = query(
      collection(db, "v3events"),
      where("projectId", "==", projectId),
      where("isActive", "==", true),
      orderBy("createdAt", "desc")
    );
    
    const unsubscribeEvents = onSnapshot(eventsQuery, (snapshot) => {
      const events = snapshot.docs.map(doc => ({ ...doc.data(), eventId: doc.id } as V3Event));
      setAvailableEvents(events.filter(e => e.isPublic));
      setMyEvents(events.filter(e => e.creatorId === userId));
    });

    // Subscribe to active rooms
    const roomsQuery = query(
      collection(db, "v3rooms"),
      where("projectId", "==", projectId),
      where("status", "in", ["waiting", "running"]),
      orderBy("createdAt", "desc")
    );
    
    const unsubscribeRooms = onSnapshot(roomsQuery, (snapshot) => {
      const rooms = snapshot.docs.map(doc => ({ ...doc.data(), roomId: doc.id } as V3Room));
      setActiveRooms(rooms.filter(r => r.isPublic || r.participants?.includes(userId)));
      setLoading(false);
    });

    return () => {
      unsubscribeEvents();
      unsubscribeRooms();
    };
  }, [projectId, navigate, firebaseUser, authLoading]);

  const handleLogout = () => {
    // With anonymous auth, just navigate back to start
    navigate(`/v3/${projectId}/start`);
  };

  const handleJoinRoom = (room: V3Room) => {
    navigate(`/v3/${projectId}/room/${room.roomId}`);
  };

  const handleCreateEvent = () => {
    navigate(`/v3/${projectId}/events/new`);
  };

  const handleEditEvent = (eventId: string) => {
    navigate(`/v3/${projectId}/events/${eventId}/edit`);
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>読み込み中...</LoadingState>
      </Container>
    );
  }

  if (!user || !project) {
    return null;
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>{project.projectName} - マイページ</Title>
          <UserInfo>
            <Username>{user.username}</Username>
            <LogoutButton onClick={handleLogout}>ログアウト</LogoutButton>
          </UserInfo>
        </HeaderContent>
      </Header>

      <Content>
        <Section>
          <SectionHeader>
            <SectionTitle>参加できるルーム</SectionTitle>
          </SectionHeader>
          {activeRooms.length > 0 ? (
            <RoomList>
              {activeRooms.map(room => {
                const event = availableEvents.find(e => e.eventId === room.eventId);
                return (
                  <RoomCard key={room.roomId}>
                    <RoomInfo>
                      <RoomName>{room.roomName}</RoomName>
                      <div>
                        <RoomStatus status={room.status}>
                          {room.status === "waiting" && "待機中"}
                          {room.status === "running" && "開催中"}
                        </RoomStatus>
                        <EventInfo>
                          イベント: {event?.eventName || "不明"}
                        </EventInfo>
                        <EventInfo>
                          参加者: {room.participants?.length || 0}人
                        </EventInfo>
                      </div>
                    </RoomInfo>
                    <JoinButton 
                      onClick={() => handleJoinRoom(room)}
                      disabled={room.status === "finished"}
                    >
                      参加する
                    </JoinButton>
                  </RoomCard>
                );
              })}
            </RoomList>
          ) : (
            <EmptyState>現在参加できるルームはありません</EmptyState>
          )}
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>参加できるイベント</SectionTitle>
          </SectionHeader>
          {availableEvents.length > 0 ? (
            <EventGrid>
              {availableEvents.map(event => (
                <EventCard key={event.eventId}>
                  <EventName>{event.eventName}</EventName>
                  {event.description && (
                    <EventInfo>{event.description}</EventInfo>
                  )}
                  <EventInfo>問題数: {event.problems?.length || 0}</EventInfo>
                  <EventInfo>作成者: {event.creatorId}</EventInfo>
                </EventCard>
              ))}
            </EventGrid>
          ) : (
            <EmptyState>参加できるイベントはありません</EmptyState>
          )}
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>作成したイベント</SectionTitle>
            <CreateButton onClick={handleCreateEvent}>
              新規イベント作成
            </CreateButton>
          </SectionHeader>
          {myEvents.length > 0 ? (
            <EventGrid>
              {myEvents.map(event => (
                <EventCard 
                  key={event.eventId}
                  onClick={() => handleEditEvent(event.eventId)}
                >
                  <EventName>{event.eventName}</EventName>
                  {event.description && (
                    <EventInfo>{event.description}</EventInfo>
                  )}
                  <EventInfo>問題数: {event.problems?.length || 0}</EventInfo>
                  <EventInfo>
                    公開設定: {event.isPublic ? "公開" : "非公開"}
                  </EventInfo>
                </EventCard>
              ))}
            </EventGrid>
          ) : (
            <EmptyState>まだイベントを作成していません</EmptyState>
          )}
        </Section>
      </Content>
    </Container>
  );
};

export default V3MyPage;